/**
 * Ospiti API Queries
 * Centralized data fetching for Guests feature
 */

import { supabase, getCurrentUserId } from '../client';
import type { 
  Ospite, OspiteInsert, OspiteUpdate,
  QueryOptions 
} from '@/types';
import { DatabaseTable } from '@/types/database.types';

const TABLE = DatabaseTable.OSPITI;

// ============================================================
// GET Queries
// ============================================================

export async function getOspiti(options?: QueryOptions & { userId?: string }) {
  const userId = options?.userId || await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  let query = supabase
    .from(TABLE)
    .select('*', { count: options?.count })
    .eq('user_id', userId);

  // Apply filters
  if (options?.filters?.eq) {
    Object.entries(options.filters.eq).forEach(([key, value]) => {
      query = query.eq(key, value);
    });
  }

  // Search by nome/cognome/email
  if (options?.filters?.search) {
    const search = options.filters.search as string;
    query = query.or(`nome.ilike.%${search}%,cognome.ilike.%${search}%,email.ilike.%${search}%`);
  }

  // Apply ordering
  if (options?.order) {
    query = query.order(options.order.column, { 
      ascending: options.order.ascending ?? true 
    });
  } else {
    query = query.order('cognome', { ascending: true });
  }

  // Apply pagination
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
  }

  const { data, error, count } = await query;

  return {
    data: data as Ospite[] | null,
    error,
    count,
  };
}

export async function getOspiteById(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('id', id)
    .eq('user_id', uid)
    .single();

  return {
    data: data as Ospite | null,
    error,
  };
}

export async function getOspitiByEmail(email: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('user_id', uid)
    .eq('email', email);

  return {
    data: data as Ospite[] | null,
    error,
  };
}

// ============================================================
// POST Queries
// ============================================================

export async function createOspite(ospite: OspiteInsert, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .insert({ ...ospite, user_id: uid })
    .select()
    .single();

  return {
    data: data as Ospite | null,
    error,
  };
}

// ============================================================
// PATCH Queries
// ============================================================

export async function updateOspite(id: string, updates: OspiteUpdate, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .update(updates)
    .eq('id', id)
    .eq('user_id', uid)
    .select()
    .single();

  return {
    data: data as Ospite | null,
    error,
  };
}

// ============================================================
// DELETE Queries
// ============================================================

export async function deleteOspite(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq('id', id)
    .eq('user_id', uid);

  return { error };
}

// ============================================================
// Stats & Analytics
// ============================================================

export async function getOspitiStats(userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { count, error } = await supabase
    .from(TABLE)
    .select('*', { count: 'exact', head: true })
    .eq('user_id', uid);

  return { count, error };
}
